import classnames from 'classnames';
import css from 'styled-jsx/css';
import Router from 'next/router';
import PropTypes from 'prop-types';

import { useState } from 'react';
import quotesApi from '../../../services/api/quotes';

import Card from '../../common/layout/Card';
import { colors } from '../../common/Theme';
import { formatter } from '@yupwego/string-fns';

import Button from '../../common/elements/Button';
import IconButton from '../../common/elements/IconButton';
import NewTraveler from './travelers/NewTraveler';
import { SIMULATION_HREF } from '../../../../pages/assurance/simulation';
import { HREF as PRICING_HREF } from '../../../../pages/assurance/simulation/tarif';

const DESKTOP_BREAKPOINT = 600;
const KNOWN_TRAVELER_ENABLED = false;

const styles = css`
  .traveler-actions {
    margin: 0;
    padding: 0;
  }

  .traveler-action-item {
    display: inline-block;
    margin-bottom: 24px;
    margin-right: 10px;
  }

  .traveler-action-item:last-of-type {
    margin-right: 0;
  }

  .traveler-action-button {
    padding: 0 0 4px;
    min-width: 200px;
    color: ${colors.ywgNavy};
    font-size: 1.125rem;
    text-align: left;
    background: no-repeat;
    border: none;
    border-bottom: 4px solid transparent;
    opacity: 0.5;
  }

  .traveler-action-button--active {
    border-bottom-color: currentColor;
    opacity: 1;
  }

  .travelers {
    margin: 0 0 50px;
    padding: 0;
    list-style: none;
  }
  .traveler {
    display: flex;
    margin: 0 0 20px;
    padding: 0;
    width: 100%;
    align-items: center;
  }
  .traveler-infos {
    display: flex;
    margin: 0;
    padding: 0;
    flex-direction: column;
    flex-grow: 1;
    font-size: 0.875rem;
    font-weight: 700;
    list-style: none;
  }

  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    .traveler-infos {
      flex-direction: row;
    }

    .traveler-infos-item {
      padding: 0 10px;
    }

    .traveler-infos-item:first-of-type {
      padding-left: 0;
    }

    .traveler-name {
      width: 30%;
    }

    .traveler-birthday {
      width: 25%;
      text-align: center;
    }

    .traveler-country {
      width: 30%;
      text-align: right;
    }
  }
`;
const embeddedStyles = css.resolve`
  .traveler-delete {
    margin: 0 40px;
  }
`;

const Travelers = ({ className, countries, quote, profile }) => {
  const [travelers, setTravelers] = useState(quote.beneficiaries);

  const [currentAction, setCurrentAction] = useState('new');
  const changeCurrentActionClicked = action => {
    setCurrentAction(action);
  };

  const [continueSubmitting, setContinueSubmitting] = useState(false);
  const canContinue = travelers.length > 0;
  const continueClicked = async () => {
    if (continueSubmitting === true) {
      return false;
    }

    Router.push(PRICING_HREF);
  };
  const onAdd = traveler => {
    const travelersUpdated = [...travelers, traveler];
    setTravelers(travelersUpdated);
    sendTravelers(travelersUpdated);
  };
  const removeTraveler = traveler => {
    const travelersUpdated = travelers.filter(s => s.key != traveler.key);
    setTravelers(travelersUpdated);
    sendTravelers(travelersUpdated);
  };

  const sendTravelers = travelers => {
    setContinueSubmitting(true);
    quotesApi
      .updateBeneficiaries(quote.id, travelers)
      .then(() => setContinueSubmitting(false));
  };

  return (
    <div
      className={classnames(
        'form',
        { 'form-continue': canContinue },
        className
      )}
    >
      <Card squared className={classnames('panel', className)}>
        {travelers.length ? (
          <>
            <h2 className={classnames('title', className)}>Les voyageurs</h2>
            <ol className="travelers">
              {travelers.map(traveler => {
                return (
                  <li key={traveler.key} className="traveler">
                    <ul className="traveler-infos">
                      <li className="traveler-infos-item traveler-name">
                        {traveler.firstname} {traveler.lastname}
                      </li>
                      <li className="traveler-infos-item traveler-birthday">
                        {formatter(traveler.birthday)}
                      </li>
                      <li className="traveler-infos-item traveler-country">
                        {traveler.country}
                      </li>
                    </ul>
                    <IconButton
                      icon="trash"
                      alt={`Supprimer ${traveler.firstname} ${traveler.lastname}`}
                      title={`Supprimer ${traveler.firstname} ${traveler.lastname}`}
                      className={classnames(
                        'traveler-delete',
                        embeddedStyles.className
                      )}
                      id={traveler.key}
                      onClick={() => removeTraveler(traveler)}
                    />
                  </li>
                );
              })}
            </ol>
          </>
        ) : null}
        {canContinue ? (
          <h2 className={classnames('title', className)}>
            Ajouter un voyageur
          </h2>
        ) : (
          <h2 className={classnames('title', className)}>Qui voyage ?</h2>
        )}
        {KNOWN_TRAVELER_ENABLED && profile ? (
          <nav>
            <ul className="traveler-actions">
              <li className="traveler-action-item">
                <button
                  className={classnames('traveler-action-button', {
                    'traveler-action-button--active': currentAction === 'new'
                  })}
                  onClick={() => changeCurrentActionClicked('new')}
                >
                  Nouveau Voyageur
                </button>
              </li>
              <li className="traveler-action-item">
                <button
                  className={classnames('traveler-action-button', {
                    'traveler-action-button--active': currentAction === 'known'
                  })}
                  onClick={() => changeCurrentActionClicked('known')}
                >
                  Voyageurs connus
                </button>
              </li>
            </ul>
          </nav>
        ) : null}
        {currentAction === 'new' ? (
          <NewTraveler countries={countries} onAdd={onAdd} />
        ) : (
          <select>
            <option>Jean Martin - 23 / 11 /1989 - France</option>
          </select>
        )}
      </Card>
      <div className={classnames('bottom-actions', className)}>
        <a
          href={SIMULATION_HREF}
          className={classnames('return-link', className)}
        >
          Retour
        </a>
        {canContinue ? (
          <Button
            className={classnames('continue', className)}
            type="button"
            arrow={continueSubmitting ? null : 'right'}
            shadowed
            submitting={continueSubmitting}
            onClick={continueClicked}
            disabled={continueSubmitting}
          >
            Continuer
          </Button>
        ) : null}
      </div>
      <style jsx>{styles}</style>
      {embeddedStyles.styles}
    </div>
  );
};

Travelers.propTypes = {
  className: PropTypes.string.isRequired,
  countries: PropTypes.array.isRequired,
  quote: PropTypes.object.isRequired,
  profile: PropTypes.object
};

export default Travelers;
